import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, money, EDITION_STYLE } from "../lib/api";

const PERKS = {
  GENESIS: ["Registry numbers 001–010 only", "Permanently limited to ten records", "Founding edition seal on certificate", "Priority placement in the public registry"],
  SIGNATURE: ["Registry numbers 011–100", "Permanently limited to ninety records", "Signature edition certificate styling", "Featured in the public registry"],
  COLLECTOR: ["Registry numbers 101–1000", "Permanently limited to nine hundred records", "Collector edition certificate styling", "Full public registry listing"],
  CELESTIAL: ["Registry numbers 1001 onward", "Open, continuing series", "Celestial edition certificate styling", "Full public registry listing"],
};

export default function Editions() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/pricing").then(({ data }) => setData(data)); }, []);

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-10 py-24" data-testid="editions-page">
      <div className="hy-label">Editions & Pricing</div>
      <h1 className="font-display text-4xl sm:text-5xl text-white mt-5">One continuous registry.<br />Four permanent editions.</h1>
      <p className="text-sm text-slate-400 mt-8 max-w-3xl leading-relaxed">
        Your edition is determined by your position in the single HYLUNER numbering sequence at the moment of
        registration. The number, once issued, is permanent and never reissued.
      </p>

      {data && (
        <div className="hy-glass px-8 py-6 mt-12 inline-flex flex-wrap gap-10" data-testid="next-number-banner">
          <div>
            <div className="hy-label">Next Registry Number</div>
            <div className="font-display text-2xl text-amber-200 mt-2">#{String(data.next_registry_number).padStart(4, "0")}</div>
          </div>
          <div>
            <div className="hy-label">Current Edition Tier</div>
            <div className="font-display text-2xl text-amber-200 mt-2">{data.next_edition}</div>
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-16">
        {(data?.editions || []).map((e) => (
          <div key={e.edition} className="hy-card p-8 flex flex-col" data-testid={`edition-card-${e.edition}`}>
            <span className={`self-start text-[0.6rem] uppercase tracking-[0.2em] px-3 py-1 border rounded-full ${EDITION_STYLE[e.edition]}`}>
              {e.edition}
            </span>
            <div className="font-mono-hy text-xs text-slate-500 mt-5">
              #{String(e.range_start).padStart(3, "0")}–{e.range_end ? String(e.range_end).padStart(3, "0") : "∞"}
            </div>
            <div className="font-display text-3xl text-white mt-4">
              {e.price_from !== e.price_to ? `${money(e.price_from)} – ${money(e.price_to)}` : money(e.price)}
            </div>
            <div className="text-xs text-slate-500 mt-2">
              {e.edition === "GENESIS" ? "individually priced per record" : "per registry record"}
            </div>
            <div className="hy-rule my-6" />
            <ul className="space-y-3 text-xs text-slate-400 flex-1">
              {PERKS[e.edition].map((p) => <li key={p} className="leading-relaxed">— {p}</li>)}
            </ul>
            <div className="mt-7">
              <div className="text-xs text-amber-300/80">
                {e.remaining === null ? "Open series" : `${e.remaining} of ${e.total} remaining`}
              </div>
              <Link to="/create" className="hy-btn block text-center px-6 py-3 text-xs uppercase mt-4">Register</Link>
            </div>
          </div>
        ))}
      </div>

      {data?.genesis_ladder && (
        <div className="hy-card p-8 mt-16" data-testid="genesis-ladder">
          <div className="hy-label">Genesis ladder · registry #001–#010</div>
          <p className="text-xs text-slate-500 mt-3 max-w-2xl leading-relaxed">
            The ten founding records are individually priced, descending from the first HYLUNER registry entry.
            Each number is permanent and issued once.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mt-8">
            {data.genesis_ladder.map((g) => (
              <div key={g.number} className={`border p-4 ${g.claimed ? "border-slate-700 opacity-50" : "border-amber-500/25"}`}
                data-testid={`ladder-${g.number}`}>
                <div className="font-mono-hy text-xs text-amber-300/80">#{String(g.number).padStart(3, "0")}</div>
                <div className="font-display text-lg text-white mt-2">{money(g.price)}</div>
                <div className="text-[0.6rem] uppercase tracking-widest text-slate-500 mt-2">
                  {g.claimed ? "Claimed" : "Available"}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="text-xs text-slate-500 mt-14 max-w-3xl leading-relaxed">
        A HYLUNER registry number denotes the position of a record in our private registry sequence only. It is
        not an astronomical ranking and confers no ownership of any celestial body.
      </p>
    </div>
  );
}
